/**
 * Admin Dashboard - Main admin panel
 * Reorganized layout: Stats → Quick Actions → Alerts → Metrics → Activity
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Camera, Building2, Play, TrendingUp, 
  AlertTriangle, CheckCircle2, Clock, Shield,
  ChevronRight, Settings, Activity, Radio, Image, ShoppingCart, LogOut, HelpCircle,
  DollarSign, CreditCard, UserPlus, Percent, Calendar, FileText, Bug, Lightbulb,
  Plus, X, Send, Loader2, ChevronDown, ChevronUp, Edit2, Save, Archive, Sparkles, Link2, Tv
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { adminAPI } from '../../services/api';
import api from '../../services/api';
import { toast } from 'sonner';
import { useAuthStore } from '../../store';

// Stat card component
const StatCard = ({ icon: Icon, label, value, subValue, trend, color = "primary", onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ scale: onClick ? 1.02 : 1 }}
    onClick={onClick}
    className={`glass rounded-xl p-5 ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className="flex items-start justify-between mb-3">
      <div className={`w-12 h-12 rounded-xl bg-${color}/10 flex items-center justify-center`}>
        <Icon className={`w-6 h-6 text-${color}`} />
      </div>
      {trend && (
        <div className="flex items-center gap-1 text-xs text-green-400">
          <TrendingUp className="w-3 h-3" />
          <span>{trend}</span>
        </div>
      )}
    </div>
    <p className="text-3xl font-bold font-display">{value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
    {subValue && (
      <p className="text-xs text-muted-foreground mt-1">{subValue}</p>
    )}
  </motion.div>
);

// Quick action card - compact for 6 items
const QuickAction = ({ icon: Icon, label, description, onClick, variant = "default" }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`w-full p-4 rounded-xl border text-left transition-colors ${
      variant === "primary" 
        ? "bg-primary/10 border-primary/30 hover:bg-primary/20" 
        : "bg-card border-border hover:bg-secondary"
    }`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        variant === "primary" ? "bg-primary/20" : "bg-secondary"
      }`}>
        <Icon className={`w-5 h-5 ${variant === "primary" ? "text-primary" : "text-muted-foreground"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{label}</p>
        <p className="text-xs text-muted-foreground truncate">{description}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
    </div>
  </motion.button>
);

// Status badge
const StatusBadge = ({ status }) => {
  const config = {
    online: { color: "bg-green-500", label: "En línea" },
    offline: { color: "bg-red-500", label: "Fuera de línea" },
    maintenance: { color: "bg-yellow-500", label: "Mantenimiento" }
  };
  
  const { color, label } = config[status] || config.offline;
  
  return (
    <span className="flex items-center gap-2 text-xs">
      <span className={`w-2 h-2 rounded-full ${color}`} />
      {label}
    </span>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [activities, setActivities] = useState([]);
  const [recentClips, setRecentClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [financialStats, setFinancialStats] = useState({
    totalRevenue: 0,
    revenueThisMonth: 0,
    revenueLastMonth: 0,
    posRevenue: 0,
    reservationsRevenue: 0,
    shopRevenue: 0
  });
  const [adoptionStats, setAdoptionStats] = useState({
    newUsersThisMonth: 0,
    newUsersLastMonth: 0,
    activeUsersThisWeek: 0,
    conversionRate: 0,
    clubsAdded: 0
  });
  
  // Bitácora (Log) state
  const [showBitacoraViewer, setShowBitacoraViewer] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [bitacoraEntries, setBitacoraEntries] = useState([]);
  const [loadingBitacora, setLoadingBitacora] = useState(false);
  const [reportType, setReportType] = useState('bug'); // bug or suggestion
  const [reportForm, setReportForm] = useState({
    title: '',
    description: '',
    category: 'general',
    severity: 'medium',
    steps_to_reproduce: ''
  });
  const [submittingReport, setSubmittingReport] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [analyzingBitacora, setAnalyzingBitacora] = useState(false);
  const [bitacoraAnalysis, setBitacoraAnalysis] = useState(null);
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  
  const handleLogout = () => {
    logout();
    navigate('/auth');
    toast.success('Sesión cerrada');
  };
  
  useEffect(() => {
    fetchData();
    fetchBitacora();
  }, []);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, camerasRes, activityRes, financialRes, adoptionRes, clipsRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getCameras(),
        adminAPI.getActivityLog(10).catch(() => ({ data: { activities: [] } })),
        adminAPI.getFinancialStats().catch(() => ({ data: {} })),
        adminAPI.getAdoptionStats().catch(() => ({ data: {} })),
        api.get('/admin/clips?limit=10').catch(() => ({ data: { clips: [] } }))
      ]);
      
      setStats(statsRes.data);
      setCameras(camerasRes.data.cameras || []);
      setActivities(activityRes.data.activities || []);
      setRecentClips(clipsRes.data.clips || []);
      if (financialRes.data) setFinancialStats(financialRes.data);
      if (adoptionRes.data) setAdoptionStats(adoptionRes.data);
    } catch (err) {
      console.error('Admin data fetch error:', err);
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };
  
  // Bitácora functions
  const fetchBitacora = async () => {
    try {
      setLoadingBitacora(true);
      const res = await api.get('/admin/bitacora');
      setBitacoraEntries(res.data.entries || []);
    } catch (err) {
      console.error('Error fetching bitacora:', err);
    } finally {
      setLoadingBitacora(false);
    }
  };
  
  const handleSubmitReport = async () => {
    if (!reportForm.title.trim() || !reportForm.description.trim()) {
      toast.error('Título y descripción son requeridos');
      return;
    }
    
    setSubmittingReport(true);
    try {
      await api.post('/admin/bitacora', {
        ...reportForm,
        type: reportType,
      });
      toast.success(reportType === 'bug' ? 'Falla reportada' : 'Sugerencia enviada');
      setShowReportDialog(false);
      setReportForm({ title: '', description: '', category: 'general', severity: 'medium', steps_to_reproduce: '' });
      fetchBitacora();
    } catch (err) {
      toast.error('Error al enviar reporte');
    } finally {
      setSubmittingReport(false);
    }
  };
  
  const handleUpdateEntry = async (entryId, updateData) => {
    try {
      await api.patch(`/admin/bitacora/${entryId}`, updateData);
      toast.success('Entrada actualizada');
      fetchBitacora();
      setEditingEntry(null);
    } catch (err) {
      toast.error('Error al actualizar');
    }
  };
  
  const handleExportForAgent = async () => {
    try {
      const res = await api.get('/admin/bitacora/export-prompt');
      // Copy to clipboard
      await navigator.clipboard.writeText(res.data.prompt);
      toast.success('Prompt copiado al portapapeles. Compártelo con el agente para evaluación.');
    } catch (err) {
      toast.error('Error al exportar');
    }
  };
  
  const handleAnalyzeBitacora = async () => {
    setAnalyzingBitacora(true);
    try {
      const res = await adminAPI.analyzeBitacora();
      if (res.data.success) {
        setBitacoraAnalysis(res.data.analysis);
        setShowAnalysisDialog(true);
        toast.success('Análisis completado');
      } else {
        toast.error(res.data.error || 'Error al analizar');
      }
    } catch (err) {
      console.error('Analysis error:', err);
      toast.error('Error al analizar la bitácora');
    } finally {
      setAnalyzingBitacora(false);
    }
  };
  
  // Format date
  const formatRelativeTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMins = Math.floor((now - date) / 60000);
    
    if (diffMins < 1) return 'Hace un momento';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffMins < 1440) return `Hace ${Math.floor(diffMins / 60)} horas`;
    return date.toLocaleDateString('es-MX');
  };
  
  const getActivityIcon = (type) => {
    switch (type) {
      case 'clip': return Play;
      case 'user': return Users;
      case 'camera': return Camera;
      default: return Activity;
    }
  };
  
  // Offline cameras for alerts
  const offlineCameras = cameras.filter(c => c.status === 'offline');
  
  // Pending bitacora count
  const pendingBitacoraCount = bitacoraEntries.filter(e => e.status === 'pending').length;
  
  return (
    <div className="min-h-screen bg-background overflow-x-hidden admin-mobile">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <Shield className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="font-display font-bold text-lg">Back Office</h1>
              <p className="text-xs text-muted-foreground">
                {user?.first_name ? `Hola, ${user.first_name}` : 'Panel de administración'}
              </p>
            </div>
          </div>
          
          <div className="w-full sm:w-auto flex flex-wrap items-center justify-end gap-2">
            {/* Bitácora Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBitacoraViewer(true)}
              className="gap-2 relative"
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Bitácora</span>
              {pendingBitacoraCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingBitacoraCount}
                </span>
              )}
            </Button>
            
            {/* Settings Button - moved from quick actions */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/settings')}
              className="gap-2"
            >
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Configuración</span>
            </Button>
            
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="gap-2 text-muted-foreground hover:text-destructive"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Cerrar sesión</span>
            </Button>
          </div>
        </div>
      </header>
      
      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Stats grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={Users}
            label="Usuarios totales"
            value={stats?.total_users || 0}
            subValue={`${stats?.premium_users || 0} premium`}
            trend="+12%"
            onClick={() => navigate('/admin/users')}
          />
          
          <StatCard
            icon={Play}
            label="Clips generados"
            value={stats?.total_clips || 0}
            subValue={`${stats?.clips_today || 0} hoy`}
            color="primary"
          />
          
          <StatCard
            icon={Camera}
            label="Cámaras en línea"
            value={`${stats?.cameras_online || 0}/${(stats?.cameras_online || 0) + (stats?.cameras_offline || 0)}`}
            subValue={stats?.cameras_offline > 0 ? `${stats.cameras_offline} fuera de línea` : "Todo operativo"}
            color={stats?.cameras_offline > 0 ? "destructive" : "primary"}
            onClick={() => navigate('/admin/cameras')}
          />
          
          <StatCard
            icon={Building2}
            label="Clubes activos"
            value={stats?.total_clubs || 0}
            subValue="En 22 ciudades"
            onClick={() => navigate('/admin/clubs')}
          />
        </div>
        
        {/* Quick Actions - 8 buttons symmetric grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <h3 className="font-display font-bold text-lg mb-3">Acciones rápidas</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <QuickAction
              icon={Users}
              label="Gestionar usuarios"
              description="Ver, buscar y modificar planes"
              onClick={() => navigate('/admin/users')}
            />
            <QuickAction
              icon={Building2}
              label="Administrar clubes"
              description="Gestionar todos los clubes"
              onClick={() => navigate('/admin/clubs')}
            />
            <QuickAction
              icon={Calendar}
              label="Reservaciones"
              description="Playtomic y reservas locales"
              onClick={() => navigate('/admin/playtomic')}
              variant="primary"
            />
            <QuickAction
              icon={ShoppingCart}
              label="E-commerce"
              description="Órdenes, ventas y estadísticas"
              onClick={() => navigate('/admin/ecommerce')}
            />
            <QuickAction
              icon={Camera}
              label="Estado de cámaras"
              description="Monitorear salud de Hikvision"
              onClick={() => navigate('/admin/cameras')}
            />
            <QuickAction
              icon={Link2}
              label="Asignar cámaras"
              description="Configurar cámara por cancha"
              onClick={() => navigate('/admin/cameras/assignments')}
            />
            <QuickAction
              icon={Radio}
              label="Botones Shelly"
              description="Configurar botones físicos"
              onClick={() => navigate('/admin/shelly')}
            />
            <QuickAction
              icon={HelpCircle}
              label="FAQ y Soporte"
              description="Gestionar preguntas frecuentes"
              onClick={() => navigate('/admin/faq')}
            />
            <QuickAction
              icon={Tv}
              label="Sponsors"
              description="Branding de videos"
              onClick={() => navigate('/admin/sponsors')}
            />
          </div>
        </motion.div>
        
        {/* Camera Alerts */}
        {offlineCameras.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-red-500/10 border border-red-500/30 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <h3 className="font-semibold text-red-400">Alertas de cámaras</h3>
            </div>
            <div className="space-y-2">
              {offlineCameras.slice(0, 3).map((camera) => (
                <div key={camera.id} className="flex items-center justify-between p-2 bg-background/50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">{camera.name || camera.channel_name || 'Cámara'}</p>
                    <p className="text-xs text-muted-foreground">{camera.club_name || 'Sin asignar'}</p>
                  </div>
                  <StatusBadge status="offline" />
                </div>
              ))}
              {offlineCameras.length > 3 && (
                <Button 
                  variant="ghost" 
                  className="w-full text-red-400" 
                  onClick={() => navigate('/admin/cameras')}
                >
                  Ver todas ({offlineCameras.length})
                </Button>
              )}
            </div>
          </motion.div>
        )}
        
        {/* Financial & Adoption Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Financial Metrics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card border border-border rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-green-500" />
              <h3 className="font-semibold">Métricas Financieras</h3>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3 bg-secondary/30 rounded-lg">
                <p className="text-xs text-muted-foreground">Ingresos este mes</p>
                <p className="text-xl font-bold text-green-500">
                  ${financialStats.revenueThisMonth?.toLocaleString('es-MX') || 0}
                </p>
              </div>
              
              <div className="p-3 bg-secondary/30 rounded-lg">
                <p className="text-xs text-muted-foreground">Total acumulado</p>
                <p className="text-xl font-bold">${financialStats.totalRevenue?.toLocaleString('es-MX') || 0}</p>
              </div>
              
              <div className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2 border-t border-border/50">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">POS</p>
                  <p className="text-sm font-medium">${financialStats.posRevenue?.toLocaleString('es-MX') || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Reservas</p>
                  <p className="text-sm font-medium">${financialStats.reservationsRevenue?.toLocaleString('es-MX') || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Tienda</p>
                  <p className="text-sm font-medium">${financialStats.shopRevenue?.toLocaleString('es-MX') || 0}</p>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Adoption Metrics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-card border border-border rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-4">
              <UserPlus className="w-5 h-5 text-blue-500" />
              <h3 className="font-semibold">Adopción de Usuarios</h3>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3 bg-secondary/30 rounded-lg">
                <p className="text-xs text-muted-foreground">Nuevos este mes</p>
                <p className="text-xl font-bold text-blue-500">+{adoptionStats.newUsersThisMonth || 0}</p>
              </div>
              
              <div className="p-3 bg-secondary/30 rounded-lg">
                <p className="text-xs text-muted-foreground">Usuarios activos (7d)</p>
                <p className="text-xl font-bold">{adoptionStats.activeUsersThisWeek || 0}</p>
              </div>
              
              <div className="p-3 bg-secondary/30 rounded-lg">
                <p className="text-xs text-muted-foreground">Tasa de conversión</p>
                <p className="text-xl font-bold flex items-center gap-1">
                  <Percent className="w-4 h-4" />
                  {adoptionStats.conversionRate?.toFixed(1) || 0}
                </p>
              </div>
              
              <div className="p-3 bg-secondary/30 rounded-lg">
                <p className="text-xs text-muted-foreground">Clubes este mes</p>
                <p className="text-xl font-bold">+{adoptionStats.clubsAdded || 0}</p>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Report Bug/Suggestion CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-r from-primary/10 to-blue-500/10 border border-primary/30 rounded-xl p-4 cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => navigate('/feedloop')}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="font-display font-bold flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                FeedLoop
              </h3>
              <p className="text-sm text-muted-foreground">
                Gestiona reportes de fallas y sugerencias del sistema
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Ver todos</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>
        </motion.div>
        
        {/* Recent Clips Section - REAL DATA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-card border border-border rounded-xl overflow-hidden"
        >
          <button
            onClick={() => navigate('/admin/clips')}
            className="w-full p-4 flex items-center justify-between hover:bg-secondary/30 transition-colors border-b border-border"
          >
            <h3 className="font-semibold flex items-center gap-2">
              <Play className="w-5 h-5 text-primary" />
              Clips Recientes
            </h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Ver todos</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </button>
          
          {recentClips.length > 0 ? (
            <div className="divide-y divide-border">
              {recentClips.slice(0, 10).map((clip, idx) => (
                <div 
                  key={clip.id || idx} 
                  className="flex items-center gap-3 p-3 hover:bg-secondary/30 cursor-pointer"
                  onClick={() => navigate('/admin/clips')}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    clip.status === 'ready' ? 'bg-green-500/20' : 
                    clip.status === 'processing' || clip.status === 'pending' || clip.status === 'retrying' ? 'bg-blue-500/20' : 
                    'bg-red-500/20'
                  }`}>
                    {clip.status === 'ready' ? (
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                    ) : clip.status === 'processing' || clip.status === 'pending' || clip.status === 'retrying' ? (
                      <Clock className="w-4 h-4 text-blue-400" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">
                      {clip.club_name || 'Club'} - Cancha {clip.court_number || '?'}
                      {clip.is_fast && <span className="ml-2 text-purple-400 text-xs">(60s)</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {clip.status === 'ready' ? 'Listo' : 
                       clip.status === 'processing' ? 'Procesando' :
                       clip.status === 'pending' ? 'Pendiente' :
                       clip.status === 'retrying' ? `Reintentando (${clip.retry_count || 1}/3)` :
                       'Error'} • {formatRelativeTime(clip.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              <Play className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No hay clips recientes</p>
            </div>
          )}
        </motion.div>
      </div>
      
      {/* Report Bug/Suggestion Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {reportType === 'bug' ? (
                <><Bug className="w-5 h-5 text-red-400" /> Reportar Falla</>
              ) : (
                <><Lightbulb className="w-5 h-5 text-yellow-400" /> Sugerir Mejora</>
              )}
            </DialogTitle>
            <DialogDescription>
              {reportType === 'bug' 
                ? 'Describe la falla que encontraste. Esta información será evaluada para correcciones futuras.'
                : 'Comparte tu idea para mejorar el sistema. Las sugerencias serán evaluadas e implementadas progresivamente.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="report_title">Título *</Label>
              <Input
                id="report_title"
                value={reportForm.title}
                onChange={(e) => setReportForm({ ...reportForm, title: e.target.value })}
                placeholder={reportType === 'bug' ? "Ej: Error al cargar imágenes" : "Ej: Agregar filtro por fecha"}
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="report_category">Categoría</Label>
                <select
                  id="report_category"
                  value={reportForm.category}
                  onChange={(e) => setReportForm({ ...reportForm, category: e.target.value })}
                  className="w-full p-2 rounded-md bg-secondary border border-border text-sm"
                >
                  <option value="general">General</option>
                  <option value="ui">Interfaz (UI)</option>
                  <option value="backend">Backend/API</option>
                  <option value="performance">Rendimiento</option>
                  <option value="security">Seguridad</option>
                  <option value="feature">Nueva funcionalidad</option>
                </select>
              </div>
              
              {reportType === 'bug' && (
                <div className="space-y-2">
                  <Label htmlFor="report_severity">Severidad</Label>
                  <select
                    id="report_severity"
                    value={reportForm.severity}
                    onChange={(e) => setReportForm({ ...reportForm, severity: e.target.value })}
                    className="w-full p-2 rounded-md bg-secondary border border-border text-sm"
                  >
                    <option value="low">Baja</option>
                    <option value="medium">Media</option>
                    <option value="high">Alta</option>
                    <option value="critical">Crítica</option>
                  </select>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="report_description">Descripción *</Label>
              <textarea
                id="report_description"
                value={reportForm.description}
                onChange={(e) => setReportForm({ ...reportForm, description: e.target.value })}
                placeholder={reportType === 'bug' 
                  ? "Describe qué pasó y cuál es el comportamiento esperado..."
                  : "Describe tu idea y cómo mejoraría el sistema..."}
                rows={4}
                className="w-full p-3 rounded-md bg-secondary border border-border text-sm resize-none"
              />
            </div>
            
            {reportType === 'bug' && (
              <div className="space-y-2">
                <Label htmlFor="report_steps">Pasos para reproducir (opcional)</Label>
                <textarea
                  id="report_steps"
                  value={reportForm.steps_to_reproduce}
                  onChange={(e) => setReportForm({ ...reportForm, steps_to_reproduce: e.target.value })}
                  placeholder="1. Ir a X página&#10;2. Hacer clic en Y&#10;3. El error aparece"
                  rows={3}
                  className="w-full p-3 rounded-md bg-secondary border border-border text-sm resize-none"
                />
              </div>
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmitReport} disabled={submittingReport}>
              {submittingReport ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Enviar</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bitácora Viewer Dialog */}
      <Dialog open={showBitacoraViewer} onOpenChange={setShowBitacoraViewer}>
        <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-3xl max-h-[88dvh] overflow-y-auto overscroll-contain pb-[calc(env(safe-area-inset-bottom)+1rem)]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Bitácora de Fallas y Sugerencias
            </DialogTitle>
            <DialogDescription>
              Gestiona los reportes de fallas y sugerencias de mejora
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setReportType('bug'); setShowReportDialog(true); }}
              >
                <Bug className="w-4 h-4 mr-1" />
                Nueva falla
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setReportType('suggestion'); setShowReportDialog(true); }}
              >
                <Lightbulb className="w-4 h-4 mr-1" />
                Nueva sugerencia
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleAnalyzeBitacora}
                disabled={analyzingBitacora || bitacoraEntries.length === 0}
                className="gap-2"
              >
                {analyzingBitacora ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Analizar con IA
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleExportForAgent}
                className="gap-2"
              >
                <Archive className="w-4 h-4" />
                Exportar para agente
              </Button>
            </div>
          </div>
          
          <div className="overflow-y-auto max-h-[50vh] space-y-3">
            {loadingBitacora ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : bitacoraEntries.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No hay entradas en la bitácora</p>
              </div>
            ) : (
              bitacoraEntries.map((entry) => (
                <div 
                  key={entry.id} 
                  className={`p-4 rounded-lg border ${
                    entry.type === 'bug' ? 'border-red-500/30 bg-red-500/5' : 'border-yellow-500/30 bg-yellow-500/5'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {entry.type === 'bug' ? (
                          <Bug className="w-4 h-4 text-red-400" />
                        ) : (
                          <Lightbulb className="w-4 h-4 text-yellow-400" />
                        )}
                        <span className="font-medium">{entry.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          entry.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                          entry.status === 'in_progress' ? 'bg-blue-500/20 text-blue-500' :
                          entry.status === 'resolved' ? 'bg-green-500/20 text-green-500' :
                          'bg-gray-500/20 text-gray-500'
                        }`}>
                          {entry.status === 'pending' ? 'Pendiente' :
                           entry.status === 'in_progress' ? 'En progreso' :
                           entry.status === 'resolved' ? 'Resuelto' : entry.status}
                        </span>
                        {entry.severity && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            entry.severity === 'critical' ? 'bg-red-600/30 text-red-400' :
                            entry.severity === 'high' ? 'bg-red-500/20 text-red-400' :
                            entry.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-500' :
                            'bg-gray-500/20 text-gray-500'
                          }`}>
                            {entry.severity}
                          </span>
                        )}
                      </div>
                      
                      {editingEntry === entry.id ? (
                        <textarea
                          defaultValue={entry.description}
                          className="w-full p-2 mt-2 text-sm bg-secondary rounded border border-border"
                          rows={3}
                          id={`edit-${entry.id}`}
                        />
                      ) : (
                        <p className="text-sm text-muted-foreground mt-1">{entry.description}</p>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {entry.category} • {formatRelativeTime(entry.created_at)}
                      </p>
                    </div>
                    
                    <div className="flex flex-col gap-1">
                      {editingEntry === entry.id ? (
                        <>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => {
                              const textarea = document.getElementById(`edit-${entry.id}`);
                              handleUpdateEntry(entry.id, { description: textarea.value });
                            }}
                          >
                            <Save className="w-4 h-4 text-green-500" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => setEditingEntry(null)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => setEditingEntry(entry.id)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <select
                            value={entry.status}
                            onChange={(e) => handleUpdateEntry(entry.id, { status: e.target.value })}
                            className="text-xs p-1 rounded bg-secondary border border-border"
                          >
                            <option value="pending">Pendiente</option>
                            <option value="in_progress">En progreso</option>
                            <option value="resolved">Resuelto</option>
                          </select>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* AI Analysis Dialog */}
      <Dialog open={showAnalysisDialog} onOpenChange={setShowAnalysisDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Análisis de Bitácora con IA
            </DialogTitle>
            <DialogDescription>
              Análisis automático de patrones, prioridades y recomendaciones
            </DialogDescription>
          </DialogHeader>
          
          {bitacoraAnalysis && (
            <div className="mt-4 space-y-4">
              <div className="bg-secondary/50 rounded-lg p-4 whitespace-pre-wrap text-sm">
                {bitacoraAnalysis}
              </div>
              
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(bitacoraAnalysis);
                    toast.success('Análisis copiado al portapapeles');
                  }}
                >
                  Copiar análisis
                </Button>
                <Button onClick={() => setShowAnalysisDialog(false)}>
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
